import { assembliesApi } from '@/api/assemblies.api';
import type { AssemblyModel } from '@/types/assembly.types';
import type { AssemblyDrawingSpec } from '@/types/assemblyDrawing.types';
import { downloadBlob } from '@/utils/download';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/** Why the drawing could not be shown — each kind gets its own in-panel message. */
export type DrawingErrorKind = 'notFound' | 'undrawable' | 'generic';

/** 404 = unknown assembly, 422 = exists but has no drawable geometry; anything else is a real failure. */
function classifyDrawingError(error: unknown): DrawingErrorKind {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 404) return 'notFound';
  if (status === 422) return 'undrawable';
  return 'generic';
}

/** NC number ascending, rows without one last, ties broken by business id. */
function compareAssemblies(a: AssemblyModel, b: AssemblyModel): number {
  if (a.nc_number != null && b.nc_number != null) return a.nc_number - b.nc_number;
  if (a.nc_number != null) return -1;
  if (b.nc_number != null) return 1;
  return a.assembly_id.localeCompare(b.assembly_id);
}

/** Filesystem-safe name for the downloaded drawing (assembly ids contain spaces and dashes). */
function drawingFilename(assembly: AssemblyModel): string {
  const stem = assembly.assembly_id.trim() || String(assembly.nc_number ?? assembly.id);
  return `${stem.replace(/[^\w.-]+/g, '_')}.png`;
}

/**
 * Loads a tool's assemblies and the drawing specification of the selected one.
 *
 * Both the list and the drawing are fetched through the authenticated `apiClient`. The
 * drawing arrives as JSON — body outlines, dimensions and panel rows — which the dialog
 * renders itself; only the "download PNG" action still asks the backend for an image.
 *
 * Pass `toolId = null` (dialog closed) to drop everything.
 */
export const useAssemblyDrawing = (toolId: string | null) => {
  const { t } = useTranslation();
  const [assemblies, setAssemblies] = useState<AssemblyModel[]>([]);
  const [selectedAssemblyId, setSelectedAssemblyId] = useState<string | null>(null);
  const [isListLoading, setIsListLoading] = useState(false);
  const [hasListError, setHasListError] = useState(false);
  const [drawingSpec, setDrawingSpec] = useState<AssemblyDrawingSpec | null>(null);
  const [isDrawingLoading, setIsDrawingLoading] = useState(false);
  const [drawingError, setDrawingError] = useState<DrawingErrorKind | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  /** Bumped by the retry buttons to re-run an effect for unchanged inputs. */
  const [listRetryToken, setListRetryToken] = useState(0);
  const [drawingRetryToken, setDrawingRetryToken] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: listRetryToken is a trigger, not a read value — bumping it re-runs the fetch for the same toolId.
  useEffect(() => {
    if (!toolId) {
      setAssemblies([]);
      setSelectedAssemblyId(null);
      setHasListError(false);
      setIsListLoading(false);
      return;
    }

    let cancelled = false;
    setIsListLoading(true);
    setHasListError(false);
    setAssemblies([]);
    setSelectedAssemblyId(null);

    assembliesApi
      .getByToolId(toolId)
      .then((rows) => {
        if (cancelled) return;
        const sorted = [...rows].sort(compareAssemblies);
        setAssemblies(sorted);
        setSelectedAssemblyId(sorted[0]?.id ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setHasListError(true);
        toast.error(t('toolManagement.assemblyDrawingListError'));
      })
      .finally(() => {
        if (!cancelled) setIsListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [toolId, listRetryToken, t]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: drawingRetryToken is a trigger, not a read value — bumping it refetches the same assembly's drawing.
  useEffect(() => {
    if (!selectedAssemblyId) {
      setDrawingSpec(null);
      setDrawingError(null);
      setIsDrawingLoading(false);
      return;
    }

    let cancelled = false;
    setIsDrawingLoading(true);
    setDrawingError(null);
    setDrawingSpec(null);

    assembliesApi
      .getDrawingData(selectedAssemblyId)
      .then((spec) => {
        if (cancelled) return;
        setDrawingSpec(spec);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const kind = classifyDrawingError(error);
        setDrawingError(kind);
        // 404/422 are data conditions the panel explains in place; only a real failure
        // (network, 5xx, timeout) is worth interrupting the user with a toast.
        if (kind === 'generic') toast.error(t('toolManagement.assemblyDrawingError'));
      })
      .finally(() => {
        if (!cancelled) setIsDrawingLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAssemblyId, drawingRetryToken, t]);

  const selectedAssembly = useMemo(
    () => assemblies.find((assembly) => assembly.id === selectedAssemblyId) ?? null,
    [assemblies, selectedAssemblyId]
  );

  const selectAssembly = useCallback((assemblyId: string) => {
    setSelectedAssemblyId(assemblyId);
  }, []);

  const retryList = useCallback(() => {
    setListRetryToken((token) => token + 1);
  }, []);

  const retryDrawing = useCallback(() => {
    setDrawingRetryToken((token) => token + 1);
  }, []);

  /** Asks the backend for the rendered sheet as PNG and saves it, panel included. */
  const downloadPng = useCallback(async () => {
    if (!selectedAssembly) return;
    setIsDownloading(true);
    try {
      const blob = await assembliesApi.getDrawing(selectedAssembly.id, 'png');
      downloadBlob(blob, drawingFilename(selectedAssembly));
    } catch {
      toast.error(t('toolManagement.assemblyDrawingDownloadError'));
    } finally {
      setIsDownloading(false);
    }
  }, [selectedAssembly, t]);

  return {
    assemblies,
    selectedAssembly,
    selectAssembly,
    isListLoading,
    hasListError,
    retryList,
    drawingSpec,
    isDrawingLoading,
    drawingError,
    retryDrawing,
    downloadPng,
    isDownloading,
  };
};
